import { useCallback, useEffect, useState } from 'react';

import { ApiError, apiRequest } from '../api/client.js';
import type { ConnectionEntry, PlatformName } from '../api/types.js';
import { EMISSION_ROWS, type EmissionRow } from '../chat/event-emission.js';
import { PlatformIcon } from './PlatformIcon.js';
import { usePreferences } from '../chat/use-preferences.js';

/**
 * Which kinds of event reach the feed (ARCHITECTURE.md §18, §98.6).
 *
 * ============================ WHY IT IS NOT A COPY ============================
 * The competitor this was modelled on offers per-platform switches for Twitch
 * hosts, watch streaks and channel point rewards, Kick subs and rewards, and
 * YouTube jewel gifts. Connaro emits none of those. Reproducing that list
 * would put controls in the Console over events that never arrive, which is
 * the claim §18 forbids and the reason the Moderator capability panel exists.
 *
 * So this is organised by EVENT rather than by platform, each row naming the
 * platforms that genuinely produce it — and a row appears only when the
 * workspace has one of those platforms connected. A workspace with only Kick
 * sees chat messages and nothing else, because that is the truth.
 *
 * Twitch hosts are absent for a stronger reason still: Twitch removed hosting
 * in 2022. No amount of building brings that row back.
 * ==========================================================================
 */

export function SpecialMessages(): React.JSX.Element {
  const { preferences, update, loaded } = usePreferences();
  const [connected, setConnected] = useState<PlatformName[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiRequest<{ connections: ConnectionEntry[] }>('/connections');
      setConnected(
        data.connections
          .filter((entry) => entry.connection !== null)
          .map((entry) => entry.platform),
      );
    } catch (cause) {
      setConnected([]);
      setError(
        cause instanceof ApiError ? cause.message : 'Connaro could not read your connections.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: EmissionRow[] =
    connected === null
      ? []
      : EMISSION_ROWS.filter((row) => row.emittedBy.some((p) => connected.includes(p)));

  const toggle = (event: string, show: boolean): void => {
    const next = show
      ? preferences.hiddenEventTypes.filter((type) => type !== event)
      : [...preferences.hiddenEventTypes, event];

    void update({ hiddenEventTypes: next });
  };

  return (
    <section className="panel" aria-labelledby="special-messages-heading">
      <div className="panel__header">
        <h2 className="panel__title" id="special-messages-heading">
          Special messages
        </h2>
      </div>

      <div className="panel__body">
        <p className="field__hint">
          Which kinds of event appear in your feed. Only the events your connected platforms
          actually send are listed — Connaro does not offer a switch for something it cannot
          receive.
        </p>

        {error === null ? null : <p className="detail__unavailable">{error}</p>}

        {connected === null ? (
          <p>Loading…</p>
        ) : rows.length === 0 ? (
          <p className="field__hint">Nothing is connected yet, so there are no events to filter.</p>
        ) : (
          <ul className="emission__list">
            {rows.map((row) => {
              const shown = !preferences.hiddenEventTypes.includes(row.event);

              return (
                <li key={row.event} className="emission__row">
                  <label className="appearance__choice">
                    <input
                      type="checkbox"
                      checked={shown}
                      disabled={!loaded}
                      onChange={(changed) => {
                        toggle(row.event, changed.target.checked);
                      }}
                    />
                    <span>{row.label}</span>
                  </label>

                  {/*
                   * The platforms that send it, as marks AND as the note below
                   * — §65 forbids meaning carried by an icon alone, and an
                   * operator needs to know WHY a row is missing a platform.
                   */}
                  <span className="emission__platforms">
                    {row.emittedBy.map((platform) => (
                      <PlatformIcon
                        key={platform}
                        platform={platform}
                        displayName={platform}
                        label={platform}
                        size={14}
                      />
                    ))}
                  </span>

                  <span className="emission__note">{row.note}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
